// src/components/SendCheck.jsx
// Send Confirmation (3.64.0, owner, Oct 22: after a document went to the
// wrong insurance company). A client turns it on in Settings → Fax Settings →
// Send Confirmation (off unless they do). Then, before a Compose fax goes,
// this asks the sender to check every receiver and every document, with a
// picture of each document's first page, and to say yes. One check covers
// the whole send. Go back leaves Compose as it was.
// Each document goes to each receiver as its own fax with its own cover
// sheet, photos joined into one; over 150 pages it goes in parts
// (routes/portal.js send-fax, lib/composeFax.js faxGroups).
import { useEffect, useRef, useState } from 'react';
import Emo, { emoLabel } from './Emo';
import { firstPageImage } from './PdfPages';

const SPLIT_OVER = 150, PART_MAX = 100;   // as lib/composeFax.js
const IMAGE = /\.(jpe?g|png)$/i, PDF = /\.pdf$/i;
const partsOf = pages => (pages > SPLIT_OVER ? Math.ceil(pages / PART_MAX) : 1);
const fmtNumber = n => { const d = String(n || '').replace(/\D/g, '').slice(-10); return d.length === 10 ? `+1 (${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}` : n; };
const pagesText = n => (n == null ? 'pages counted when it goes' : `${n} page${n === 1 ? '' : 's'}`);

// 3.64.0 (owner, Oct 23): the faxes a send makes, in attachment order: each
// PDF or Word file its own, photos joined into one (as lib/composeFax.js)
export function faxGroups(files) {
  const out = [];
  let photos = null;
  for (const x of files) {
    if (IMAGE.test(x.file.name)) { if (!photos) out.push(photos = []); photos.push(x); }
    else out.push([x]);
  }
  return out;
}
const groupPages = g => (g.every(x => x.pages != null) ? g.reduce((n, x) => n + x.pages, 0) : null);
// How many faxes a send makes (receivers × documents, parts counted)
export const faxCount = (receivers, files) => receivers.length * faxGroups(files).reduce((n, g) => n + partsOf(groupPages(g) || 0), 0);

function Thumb({ file, onOpen }) {
  const [src, setSrc] = useState(undefined);   // undefined = drawing, null = no picture
  useEffect(() => {
    let gone = false, url = null;
    if (IMAGE.test(file.name)) { url = URL.createObjectURL(file); setSrc(url); }
    else if (PDF.test(file.name)) firstPageImage(file, 360).then(u => { if (!gone) setSrc(u); });
    else setSrc(null);   // Word: pictured only once it's turned into a PDF
    return () => { gone = true; if (url) URL.revokeObjectURL(url); };
  }, [file]);
  if (src === undefined) return <div style={{ ...S.thumb, ...S.thumbEmpty }} aria-label="Drawing page 1"><span style={S.dots}>…</span></div>;
  if (!src) return <div style={{ ...S.thumb, ...S.thumbEmpty }}><Emo e="📄" style={{ fontSize: 30 }} /></div>;
  return (
    <button type="button" onClick={() => onOpen(src)} style={{ ...S.thumb, ...S.thumbBtn }} aria-label={`Page 1 of ${file.name}, click to see it larger`} title="Click to see it larger">
      <img src={src} alt="" style={S.thumbImg} />
    </button>
  );
}

export default function SendCheck({ receivers, files, cover, scheduleAt, onClose }) {
  const backRef = useRef(null);
  const boxRef = useRef(null);
  const [big, setBig] = useState(null);   // { src, name }
  const faxes = faxCount(receivers, files);
  const groups = faxGroups(files);
  const many = receivers.length > 1;
  const when = scheduleAt ? new Date(scheduleAt).toLocaleString('en-US', { dateStyle: 'medium', timeStyle: 'short' }) : '';

  useEffect(() => {
    const back = document.activeElement;
    backRef.current?.focus();   // a check: Enter alone never sends
    return () => back?.focus?.();
  }, []);
  function onKeyDown(e) {
    e.stopPropagation();
    if (e.key === 'Escape') { e.preventDefault(); if (big) setBig(null); else onClose(false); }
    if (e.key === 'Tab') {
      const items = [...boxRef.current.querySelectorAll('button')].filter(el => !el.disabled);
      const i = items.indexOf(document.activeElement);
      e.preventDefault();
      items[(i + (e.shiftKey ? -1 : 1) + items.length) % items.length]?.focus();
    }
  }
  const yes = `${scheduleAt ? '🕒 Yes, schedule' : '📤 Yes, send'}${faxes > 1 ? ` ${faxes} faxes` : ''}`;

  return (
    <div style={S.overlay} onMouseDown={e => { if (e.target === e.currentTarget) onClose(false); }}>
      <div ref={boxRef} style={S.dialog} role="alertdialog" aria-modal="true" aria-labelledby="sendcheck-title" onKeyDown={onKeyDown}>
        <h3 id="sendcheck-title" style={S.title}><Emo e="🛡️" /> Check before sending{faxes > 1 ? `: ${faxes} faxes` : ''}</h3>
        <div style={S.body}>
          <div style={S.label}>To</div>
          <ul style={S.list}>
            {receivers.map(r => (
              <li key={r.number} style={S.rcv}>
                {r.name ? <><strong>{r.name}</strong> · {fmtNumber(r.number)}</> : <><strong>{fmtNumber(r.number)}</strong> <span style={S.muted}>(no name)</span></>}
              </li>
            ))}
          </ul>
          <div style={S.label}>{groups.length > 1 ? `Documents (${groups.length})` : 'Document'}</div>
          <ul style={S.list}>
            {groups.map((g, i) => {
              const pages = groupPages(g), parts = partsOf(pages || 0);
              return (
                <li key={`${g[0].file.name}-${i}`} style={S.doc}>
                  {/* photos joined into one fax: each pictured, side by side */}
                  <div style={S.thumbs}>{g.map((x, k) => <Thumb key={k} file={x.file} onOpen={src => setBig({ src, name: x.file.name })} />)}</div>
                  <div style={{ minWidth: 0 }}>
                    <div style={S.docName}>{groups.length > 1 ? `${i + 1}. ` : ''}{g.length > 1 ? `${g.length} photos, joined into one fax` : g[0].file.name}</div>
                    {g.length > 1 && <div style={S.muted}>{g.map(x => x.file.name).join(', ')}</div>}
                    <div style={S.muted}>{pagesText(pages)}{parts > 1 ? ` · goes in ${parts} parts` : ''}</div>
                  </div>
                </li>
              );
            })}
          </ul>
          <p style={S.note}>
            {groups.length > 1 ? 'Each document goes as its own fax' : 'The document goes as one fax'}{cover ? `${groups.length > 1 ? ', each' : ''} with its own cover sheet` : ', without a cover sheet'}{many ? ', to each receiver' : ''}.
            {groups.some(g => partsOf(groupPages(g) || 0) > 1) ? ' A document over 150 pages goes in parts, each with its own cover sheet, one after another.' : ''}
            {when ? ` Scheduled for ${when}.` : ''}
          </p>
          <p style={S.ask}>{many ? 'Are these the right receivers' : 'Is this the right receiver'} for {groups.length > 1 ? 'these documents' : 'this document'}?</p>
        </div>
        <div style={S.buttons}>
          <button ref={backRef} onClick={() => onClose(false)} style={S.backBtn}>Go back</button>
          <button onClick={() => onClose(true)} style={S.yesBtn}>{emoLabel(yes)}</button>
        </div>
        {big && (
          <div style={S.bigWrap} onMouseDown={e => { if (e.target === e.currentTarget) setBig(null); }}>
            <div style={S.bigBox}>
              <div style={S.bigHead}><span style={S.docName}>{big.name} · page 1</span><button onClick={() => setBig(null)} style={S.bigX} aria-label="Close the larger picture">✕</button></div>
              <img src={big.src} alt={`Page 1 of ${big.name}`} style={S.bigImg} />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

const S = {
  overlay: { position: 'fixed', inset: 0, background: 'rgb(var(--c-0f2823-rgb) / 0.45)', zIndex: 2100, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 },
  dialog: { position: 'relative', background: 'var(--c-ffffff)', borderRadius: 12, padding: '22px 24px', width: 'min(520px, 100%)', maxHeight: 'calc(100vh - 32px)', display: 'flex', flexDirection: 'column', boxSizing: 'border-box',
    boxShadow: '0 20px 60px rgb(var(--c-000000-rgb) / 0.25)', animation: 'badgePop 0.2s ease-out' },
  title: { margin: 0, fontSize: 17, fontWeight: 700, color: 'var(--t-263238)', fontFamily: 'inherit' },
  body: { overflowY: 'auto', marginTop: 12, minHeight: 0 },
  label: { fontSize: 11, fontWeight: 700, letterSpacing: '.06em', textTransform: 'uppercase', color: 'var(--t-78909c)', margin: '6px 0 6px' },
  list: { listStyle: 'none', margin: '0 0 10px', padding: 0, display: 'flex', flexDirection: 'column', gap: 6 },
  rcv: { fontSize: 14, color: 'var(--t-263238)', padding: '7px 10px', background: 'var(--c-f0faf8)', border: '1px solid var(--c-e0f2f1)', borderRadius: 8, overflowWrap: 'anywhere' },
  doc: { display: 'flex', gap: 12, alignItems: 'center', padding: 8, border: '1px solid var(--c-eceff1)', borderRadius: 8 },
  thumbs: { display: 'flex', gap: 6, flexWrap: 'wrap', flex: '0 1 auto', maxWidth: '55%' },
  docName: { fontSize: 14, fontWeight: 600, color: 'var(--t-263238)', overflowWrap: 'anywhere' },
  muted: { fontSize: 12, color: 'var(--t-78909c)', marginTop: 2 },
  thumb: { width: 64, height: 83, flex: '0 0 auto', borderRadius: 4, border: '1px solid var(--c-cfd8dc)', background: '#ffffff', overflow: 'hidden', boxSizing: 'border-box' },
  thumbEmpty: { display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--c-f5f7f8)' },
  thumbBtn: { padding: 0, cursor: 'zoom-in', transition: 'transform .15s ease' },
  thumbImg: { width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'top', display: 'block' },
  dots: { color: 'var(--t-90a4ae)', fontSize: 18 },
  note: { margin: '4px 0 0', fontSize: 13, color: 'var(--t-546e7a)', lineHeight: 1.5 },
  ask: { margin: '12px 0 0', fontSize: 14, fontWeight: 700, color: 'var(--t-263238)' },
  buttons: { display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 18, flexWrap: 'wrap' },
  backBtn: { padding: '9px 18px', background: 'var(--c-ffffff)', color: 'var(--t-37474f)', border: '1.5px solid var(--c-cfd8dc)', borderRadius: 8, cursor: 'pointer', fontSize: 14, fontWeight: 600, fontFamily: 'inherit' },
  yesBtn: { padding: '9px 18px', background: 'var(--c-00897b)', color: 'var(--t-ffffff)', border: 'none', borderRadius: 8, cursor: 'pointer', fontSize: 14, fontWeight: 600, fontFamily: 'inherit' },
  bigWrap: { position: 'absolute', inset: 0, background: 'rgb(var(--c-0f2823-rgb) / 0.55)', borderRadius: 12, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 12 },
  bigBox: { background: 'var(--c-ffffff)', borderRadius: 10, padding: 10, maxWidth: '100%', maxHeight: '100%', display: 'flex', flexDirection: 'column', gap: 8, boxSizing: 'border-box' },
  bigHead: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 },
  bigX: { background: 'none', border: 'none', fontSize: 16, cursor: 'pointer', color: 'var(--t-546e7a)' },
  bigImg: { maxWidth: '100%', maxHeight: 'calc(100vh - 140px)', objectFit: 'contain', border: '1px solid var(--c-eceff1)', minHeight: 0 },
};
